import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiPhone, FiMail, FiMapPin, FiMessageCircle, FiSend,
  FiChevronRight, FiClock, FiCheckCircle,
} from 'react-icons/fi';
import { useSettings } from '../context/SettingsContext';
import API from '../api/axios';
import toast from 'react-hot-toast';

const ContactPage = () => {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) return toast.error('Name and message are required');
    if (!form.email && !form.phone) return toast.error('Please provide an email or phone number');
    setLoading(true);
    try {
      await API.post('/contact', form);
      setSent(true);
      toast.success('Message sent! We\'ll get back to you shortly.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const infoCards = [
    {
      icon: FiPhone,
      label: 'Phone',
      lines: [settings.phone, settings.phone2].filter(Boolean),
      color: 'bg-primary-100 text-primary-700',
    },
    {
      icon: FiMail,
      label: 'Email',
      lines: [settings.email].filter(Boolean),
      color: 'bg-blue-100 text-blue-700',
    },
    {
      icon: FiMapPin,
      label: 'Address',
      lines: [settings.address, settings.city, settings.country].filter(Boolean),
      color: 'bg-green-100 text-green-700',
    },
    {
      icon: FiClock,
      label: 'Business Hours',
      lines: ['Mon – Sat: 9:00 AM – 6:00 PM', 'Sun: Closed'],
      color: 'bg-orange-100 text-orange-700',
    },
  ];

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero */}
      <div className="bg-primary-800 text-white py-12">
        <div className="container-custom">
          <nav className="flex items-center gap-1.5 text-sm text-primary-300 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <FiChevronRight size={12} />
            <span className="text-white">Contact Us</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-extrabold mb-2">Get in Touch</h1>
          <p className="text-primary-200 text-base max-w-xl">
            Have questions about our products or need a custom quote? Our team is ready to help you find the right medical equipment.
          </p>
        </div>
      </div>

      <div className="container-custom py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left – Info cards */}
          <div className="space-y-4">
            {infoCards.map(({ icon: Icon, label, lines, color }) => (
              <div key={label} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
                  <Icon size={20} />
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-sm mb-1">{label}</p>
                  {lines.map((l, i) => (
                    <p key={i} className="text-sm text-gray-600">{l}</p>
                  ))}
                </div>
              </div>
            ))}

            {/* WhatsApp CTA */}
            {settings.whatsapp && (
              <a
                href={`https://wa.me/${settings.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-3 bg-green-500 hover:bg-green-600 text-white rounded-2xl p-5 transition-colors shadow-sm"
              >
                <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                  <FiMessageCircle size={22} />
                </div>
                <div>
                  <p className="font-bold text-sm">Chat on WhatsApp</p>
                  <p className="text-green-100 text-xs mt-0.5">Quick response guaranteed</p>
                </div>
              </a>
            )}
          </div>

          {/* Right – Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
              {sent ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FiCheckCircle size={32} className="text-green-500" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-800 mb-2">Message Sent!</h2>
                  <p className="text-gray-500 text-sm mb-6">Thank you for reaching out. We'll get back to you within 24 hours.</p>
                  <button onClick={() => { setSent(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }} className="btn-primary">
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-xl font-bold text-gray-800 mb-1">Send us a Message</h2>
                  <p className="text-sm text-gray-500 mb-6">Fill in the form below and we'll respond within 24 hours.</p>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1.5 block">Full Name *</label>
                        <input
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          className="input-field"
                          placeholder="Your full name"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1.5 block">Subject</label>
                        <input
                          name="subject"
                          value={form.subject}
                          onChange={handleChange}
                          className="input-field"
                          placeholder="e.g. Product inquiry, Bulk order"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                          Email Address
                          <span className="text-gray-400 font-normal ml-1">(or phone)</span>
                        </label>
                        <input
                          name="email"
                          type="email"
                          value={form.email}
                          onChange={handleChange}
                          className="input-field"
                          placeholder="you@email.com"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                          Phone Number
                          <span className="text-gray-400 font-normal ml-1">(or email)</span>
                        </label>
                        <input
                          name="phone"
                          type="tel"
                          value={form.phone}
                          onChange={handleChange}
                          className="input-field"
                          placeholder="+92 3xx xxxxxxx"
                        />
                      </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-xs text-amber-700">
                      Please provide at least one contact method (email or phone) so we can reach you.
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1.5 block">Message *</label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="input-field resize-none"
                        placeholder="Tell us about your requirement, product questions, bulk order inquiry, or anything else..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-primary w-full py-3.5 flex items-center justify-center gap-2 text-base"
                    >
                      {loading ? (
                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <><FiSend size={16} /> Send Message</>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
